/**
 * NAV-APP
 * Container for account application
 * First level of navigation (with visible menu)
 */

import { connect } from "react-redux"
import { motion, useReducedMotion } from "framer-motion"
import { withTranslation, WithTranslation } from "react-i18next"
import NavItem from "@/components/nav-item"
import {
  faChartBar,
  faClipboardList,
  faCog,
  faCrown,
  faFileAlt,
  faFilm,
  faHome,
  faPaperPlane,
  faShieldHalved,
  faTasks,
  faUser,
  faUserFriends,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import ListItem from "@/components/list-item"
import NavAvatar from "@/components/nav-avatar"
import Space from "@/components/space"
import { Session } from "@/redux/_session.types"
import logout from "@/utils/logout.utils"
import { useEffect, useState } from "react"
import MenuAccountsWidget from "@/widgets/menu-accounts.widget"
import AccountLogoWidget from "@/widgets/account-logo.widget"
import backToSupervisor from "@/utils/back-to-supervisor.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import HomeWelcomeModal from "@/modals/home-welcome.modal"
import { RotateInDownLeft, ZoomIn } from "@/utils/animations.utils"
import { NavigateFunction, useNavigate, useLocation, Link as RouterLink } from "react-router-dom"
import AccountExpiredModal from "@/modals/account-expired.modal"
import ModalConfirm from "@/components/modal-confirm"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { roleFetch } from "@/redux/role.actions"
import { roleActions } from "@/redux/role.reducers"
import { accountCleanDemo } from "@/redux/account.actions"
import { ACCOUNT_DEMO } from "@/redux/account.types"
import { STATUS_SAVING } from "@/redux/_status.types"
import { toast } from "react-toastify"
import env from "@/env"

interface StateProps extends WithTranslation {
  _session: Session
}

interface OwnProps {
  children: any //Content inside the page
}

type Props = StateProps & OwnProps

export interface NavItem {
  text: string
  icon?: IconDefinition
  pathname: string
  subItems?: NavItem[]
  onClick?: () => void
  asChip?: boolean
}

// Preserve expansion across route changes, which remount NavApp.
let previousRolesMenu = { accountId: "", expanded: false }

function NavApp(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const roles = useAppSelector((state) => state.roles)
  const rolesEnabled = props._session.userRole === "ADMIN" && props._session.accountOptions.roleManagementEnabled
  const reduceMotion = useReducedMotion()
  const rolesMenuExpanded = Boolean(rolesEnabled && location.pathname.startsWith("/adm/restricted-access") && roles.accountId === props._session.accountId && roles.list.length)
  const [rolesMenuInitiallyExpanded] = useState(() => previousRolesMenu.accountId === props._session.accountId && previousRolesMenu.expanded)

  useEffect(() => {
    previousRolesMenu = { accountId: props._session.accountId, expanded: rolesMenuExpanded }
  }, [props._session.accountId, rolesMenuExpanded])

  useEffect(() => {
    if (!rolesEnabled) {
      if (roles.accountId) dispatch(roleActions.reset())
      return
    }
    if (location.pathname.startsWith("/adm/")) dispatch(roleFetch())
  }, [dispatch, location.pathname, rolesEnabled, props._session.accountId, roles.accountId, roles.status])

  //Detect if onboarding modal is visible
  const searchParams: URLSearchParams = new URLSearchParams(window.location.search)
  const isWelcomeMode = searchParams.get("modal") === "welcome" && props._session.interfaceType !== "ADMIN_PARENT"

  //Compte expiré
  const isExpired =
    !props._session.supervisorId && props._session.accountLastSubscriptionEnd
      ? props._session.accountLastSubscriptionEnd < Date.now()
      : false

  //Sub items for tasks
  const tasksItems = [
    { text: "tasks_actions_all", pathname: "/tasks/all", icon: faClipboardList },
    { text: "tasks_all", pathname: "/tasks/home", icon: faUserFriends },
    { text: "tasks_self", pathname: "/tasks/self", icon: faUser }
  ]

  //Get admin menu
  const adminItems: NavItem[] = getAdminItems()

  //Get navItems for the current feature
  const navItems: NavItem[] = getNavItems()

  //Current dialog
  const [currentModal, setCurrentModal] = useState<"MODAL_WELCOME" | "MODAL_UNITS" | "MODAL_CLEAN_DEMO" | null>(
    isWelcomeMode ? "MODAL_WELCOME" : null
  )
  const [isDemoCleanLoading, setIsDemoCleanLoading] = useState(false)

  //Animation for the buttom
  const [alternativeOwlAnimation, setAlternativeOwlAnimation] = useState<boolean>(false)

  const MAX_VISIBLE_ITEMS = 10
  const ITEM_HEIGHT = 40

  //Detect if user is log or not
  //If not log out
  useEffect(() => {
    if (props._session.authLevel < 1) {
      logout(true)
    }
  }, [props._session.authLevel])

  //Trigger animation when modal is closed
  function closeWelcomeModal() {
    setAlternativeOwlAnimation(true)
    setCurrentModal(null)
  }

  function returnToSupervisor() {
    if (props._session.accountId === ACCOUNT_DEMO.id) {
      setCurrentModal("MODAL_CLEAN_DEMO")
    } else {
      backToSupervisor()
    }
  }

  async function cleanDemoAndReturn() {
    setIsDemoCleanLoading(true)
    const response: any = await store.dispatch(accountCleanDemo())

    if (response.error) {
      setIsDemoCleanLoading(false)
      toast("Le nettoyage du compte Smartlink a échoué.", { type: "error" })
      return
    }

    backToSupervisor()
  }

  //Get items for the admin menu
  function getAdminItems() {
    let items: NavItem[] = []

    //For superadmin (consulting account)
    if (props._session.interfaceType === "ADMIN_PARENT") {
      items = items.concat([
        { text: "superadmin_users", pathname: "/superadmin/users" },
        { text: "superadmin_templates", pathname: "/superadmin/templates" },
        { text: "utils_params", pathname: "/superadmin/settings" }
      ])
    }
    //For admin
    //> Roles (in admin or admin for one app)
    //> Users
    else if (props._session.userRole === "ADMIN") {
      items = items.concat([
        ...(props._session.accountOptions.roleManagementEnabled
          ? [
              { text: "roles_administrators", pathname: "/adm/roles", icon: faCrown },
              { text: "roles_restricted_access", pathname: "/adm/restricted-access", icon: faShieldHalved }
            ]
          : [{ text: "roles", pathname: "/adm/roles", icon: faCrown }]),
        { text: "users", pathname: "/adm/users", icon: faUserFriends },
        { text: "account_settings", pathname: "/adm/settings", icon: faCog }
      ])
    }

    return items
  }

  //Get items for the menu top
  function getNavItems() {
    //Init nav items
    const items: NavItem[] = []

    //Init home
    const homeItem = {
      text: "utils_home",
      pathname: "/home",
      icon: faHome
    }

    //Survey menu
    if (props._session.featureConnected === "survey") {
      if (props._session.featuresList.includes("survey")) {
        //Init report item
        const reportItem = {
          text: "reports",
          pathname: "/report/list",
          icon: faFileAlt
        }

        //Admins accès
        if (props._session.userRole === "ADMIN") {
          //Add templates and results
          items.push({
            text: "templates",
            pathname: "/survey/templates",
            icon: faClipboardList
          })

          //Add project if admin
          if (props._session.interfaceType === "ADMIN") {
            items.push({
              text: "projects",
              pathname: "/project/list",
              icon: faPaperPlane
            })
          }

          //Add results if admin or account is not type consulting (not useful)
          if (!props._session.isSuperAccount || props._session.interfaceType === "ADMIN") {
            items.push({
              text: "dashboard_results",
              pathname: "/dashboard/home",
              icon: faChartBar
            })

            //Add reports
            items.push(reportItem)
          }
        } else if (props._session.userRole === "OBSERVER") {
          items.push(homeItem)
          if (!props._session.accountOptions.hideReportForObserver) {
            items.push(reportItem)
          }
        }
      }

      //Add DUERP if both survey and risks are activated
      if (props._session.featuresList.includes("survey") && props._session.featuresList.includes("risks")) {
        items.push({
          text: "DUERP",
          pathname: "/duerp",
          icon: faShieldHalved
        })
      }

      //Add tasks
      if (props._session.featuresList.includes("survey") && props._session.modules.survey.task) {
        if (props._session.userRole === "ADMIN") {
          items.push({
            text: "tasks",
            pathname: "/tasks/all",
            icon: faTasks,
            subItems: tasksItems
          })
        } else {
          items.push({
            text: "tasks",
            pathname: "/tasks/self",
            icon: faTasks
          })
        }
      }

      //Learning
      if (props._session.featuresList.includes("survey") && props._session.modules.survey.learning) {
        items.push({
          text: "E-learning",
          pathname: "/learning",
          icon: faFilm
        })
      }
    }

    return items
  }

  function getSubItemIcon(icon: IconDefinition | undefined) {
    if (icon) {
      return (
        <FontAwesomeIcon
          className="mt-[2px] w-[30px] mr-[8px]"
          style={{ color: props._session.accountColors.brandPrimary }}
          icon={icon}
        />
      )
    } else {
      return null
    }
  }

  //List of menuLeftItems
  //Show for tasks or adm
  function getMenuLeftItems(): NavItem[] {
    const pathname = window.location.pathname
    if (pathname.startsWith("/tasks/") && props._session.userRole === "ADMIN") return tasksItems
    else if (pathname.startsWith("/adm/")) return adminItems
    else return []
  }

  //Detect if it is an admin page
  //menu left and specific margin
  function showMenuLeft() {
    return getMenuLeftItems().length > 0
  }

  function renderOwlFigure() {
    return (
      <div
        className="abs _hover"
        style={{
          right: 30,
          bottom: 20,
          height: 60,
          width: 60,
          borderRadius: 30,
          backgroundColor: props._session.accountColors.active,
          zIndex: 10
        }}
        onClick={() => setCurrentModal("MODAL_WELCOME")}>
        <img
          src={require("@/assets/assistant/owl_female_face.png")}
          alt={"owl assistant"}
          width={60}
          style={{ objectFit: "contain" }}
        />
      </div>
    )
  }

  return (
    <div>
      <MenuAccountsWidget />

      {isExpired && <AccountExpiredModal expirationDate={props._session.accountLastSubscriptionEnd} />}

      {currentModal === "MODAL_WELCOME" && props._session.featureConnected === "survey" && (
        <HomeWelcomeModal onClose={() => closeWelcomeModal()} />
      )}

      {currentModal === "MODAL_CLEAN_DEMO" && (
        <ModalConfirm
          status={isDemoCleanLoading ? STATUS_SAVING : undefined}
          onNo={() => backToSupervisor()}
          onYes={() => cleanDemoAndReturn()}
          text="Souhaitez-vous nettoyer le compte Smartlink avant de retourner à l'espace gestionnaire IDTree ?"
          textBold="Le nettoyage supprime les projets, questionnaires, rapports, unités de travail et risques créés pendant la démonstration."
        />
      )}

      {/** header */}
      <div
        className="flex page-navigation-menutop grey-bor"
        style={{
          background:
            "linear-gradient(90deg, " +
            props._session.accountColors.brandPrimary +
            " 0%, " +
            props._session.accountColors.brandSecondary +
            " 100%)",
          paddingLeft: props._session.connectedAsSuperadmin ? "52px" : "0px",
          width: props._session.connectedAsSuperadmin ? "calc(100% - 52px)" : "100%"
        }}>
        <AccountLogoWidget />

        {
          /** loop on menu items */
          navItems.map((item, index) => {
            const needsScroll = (item.subItems?.length ?? 0) > MAX_VISIBLE_ITEMS

            return (
              <NavItem
                key={`${item.pathname}-${index}`}
                icon={item.icon}
                tooltip={t(item.text)}
                pathname={item.pathname}
                asChip={item.asChip}
                title={t(item.text)}
                onClick={item.onClick}>
                {item.subItems && item.subItems.length > 0 && (
                  <div
                    className={[
                      "flex flex-col",
                      needsScroll ? "overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-black/20" : ""
                    ].join(" ")}
                    style={needsScroll ? { maxHeight: `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT}px` } : undefined}>
                    {item.subItems.map((subItem, i) => (
                      <ListItem
                        key={i}
                        isSmall
                        isEditable
                        onClick={subItem.onClick ?? (() => navigate(subItem.pathname))}
                        className="flex items-center gap-3 h-[40px]">
                        <div className="w-5 flex-shrink-0 text-primary flex items-center justify-center">
                          {getSubItemIcon(subItem.icon)}
                        </div>
                        <div className="flex-1 text-sm leading-tight line-clamp-2" title={t(subItem.text)}>
                          {t(subItem.text)}
                        </div>
                      </ListItem>
                    ))}
                  </div>
                )}
              </NavItem>
            )
          })
        }

        <Space />

        {adminItems.length > 0 && (
          <NavItem
            isMenuRightAlign
            icon={faCog}
            pathname={adminItems[0].pathname}
            title={
              props._session.accountName.length > 50
                ? props._session.accountName.slice(0, 45) + " [...]"
                : props._session.accountName
            }>
            {adminItems.map((adminItem, i) => (
              <div key={i}>
                <ListItem isSmall isEditable onClick={adminItem.onClick ?? (() => navigate(adminItem.pathname))}>
                  {getSubItemIcon(adminItem.icon)}
                  {t(adminItem.text)}
                </ListItem>
              </div>
            ))}
          </NavItem>
        )}

        <div style={{ width: 12 }} />

        <NavAvatar>
          {props._session.supervisorLevel > 0 ? (
            <a
              className="flex page-navigation-subitem"
              href={new URL("/pricing-idtree-f52fcfd2f738cd39b132d85594d2fc90.html", env.REACT_APP_URL_WEBSITE).href}
              target="_blank"
              rel="noopener noreferrer">
              <div className="context-menu-item">Configurateur de devis</div>
            </a>
          ) : (
            <div className="flex page-navigation-subitem" onClick={() => navigate("/adm/self")}>
              <div className="context-menu-item">{t("self_params")}</div>
            </div>
          )}
          <div className="flex page-navigation-subitem" onClick={() => logout(false)}>
            <div className="context-menu-item">{t("utils_logout")}</div>
          </div>
          {props._session.connectedAsSupervisor && (
            <div className="flex page-navigation-subitem" onClick={() => returnToSupervisor()}>
              <div className="context-menu-item">Retour (gestionnaire IDTree)</div>
            </div>
          )}
        </NavAvatar>
      </div>

      <div className="page-navigation page-navigation-withheader">
        {props._session.accountLastSubscriptionEnd && props._session.accountLastSubscriptionEnd < Date.now() && (
          <div
            className="flex flex-dcol"
            style={{
              width: "100%",
              height: 40,
              backgroundColor: "#eb5a46", //"#df1324",
              color: "white",
              zIndex: 100
            }}>
            <Space />
            <div
              className="_hover"
              style={{
                marginLeft: props._session.supervisorLevel > 0 ? 62 : 10
              }}>
              <b>
                {t("account_subscription_expired_on", {
                  date: new Date(props._session.accountLastSubscriptionEnd).toLocaleDateString()
                })}
              </b>
            </div>
            <Space />
          </div>
        )}

        <div className="flex">
          {props._session.connectedAsSuperadmin && <div style={{ width: "52px" }} />}
          {showMenuLeft() && (
            <div className="page-navigation-menuleft">
              {getMenuLeftItems().map((page) => (
                <div key={page.pathname}>
                  <div onClick={() => navigate(page.pathname)} className="page-navigation-menuleft-item">
                    <span
                      className={
                        location.pathname.indexOf(page.pathname) > -1 ? "page-navigation-menuleft-item-active" : ""
                      }>
                      {getSubItemIcon(page.icon)}
                      {t(page.text)}
                    </span>
                  </div>
                  {/** La liste des roles ne se deplie que sur la page Acces restreints. */}
                  {page.pathname === "/adm/restricted-access" && rolesEnabled && roles.accountId === props._session.accountId && (
                    <motion.div
                      initial={{ height: rolesMenuInitiallyExpanded ? "auto" : 0 }}
                      animate={{ height: rolesMenuExpanded ? "auto" : 0 }}
                      transition={{ duration: reduceMotion ? 0 : 0.2, ease: "easeInOut" }}
                      style={{ overflow: "hidden", pointerEvents: rolesMenuExpanded ? undefined : "none" }}
                      aria-hidden={!rolesMenuExpanded}>
                      {roles.list.map((role, index) => (
                        <RouterLink
                          key={role.id}
                          tabIndex={rolesMenuExpanded ? 0 : -1}
                          to={`/adm/restricted-access/${role.id}`}
                          className="page-navigation-menuleft-item"
                          style={{ display: "block", padding: "8px 10px 8px 48px", marginBottom: index === roles.list.length - 1 ? 16 : undefined }}
                          aria-current={location.pathname === `/adm/restricted-access/${role.id}` ? "page" : undefined}>
                          <div
                            className={
                              "flex grey-t" +
                              (location.pathname === `/adm/restricted-access/${role.id}`
                                ? " page-navigation-menuleft-item-active"
                                : "")
                            }>
                            <span className="flex1">{role.name}</span>
                            <span>
                              <FontAwesomeIcon icon={faUser} />
                              &nbsp;{role.usersCount}
                            </span>
                          </div>
                        </RouterLink>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            id="page-navigation-scrollable"
            className="flex1 page-navigation-children page-navigation-scrollable flex flex-dcol"
            style={{ height: "calc(100vh - 88px)" }}>
            <div className="flex">
              {!showMenuLeft() && <Space />}
              <div className={showMenuLeft() ? "flex1" : ""} style={{ width: showMenuLeft() ? undefined : "1000px" }}>
                {props.children}
              </div>
              {!showMenuLeft() && <Space />}
            </div>

            <div className="height-60" />
          </div>
        </div>
      </div>

      {props._session.userRole === "ADMIN" &&
        props._session.interfaceType === "ADMIN" &&
        props._session.featureConnected === "survey" &&
        ["/home", "/survey/templates"].includes(window.location.pathname) &&
        currentModal !== "MODAL_WELCOME" &&
        (alternativeOwlAnimation ? (
          <RotateInDownLeft>
            <ZoomIn>{renderOwlFigure()}</ZoomIn>
          </RotateInDownLeft>
        ) : (
          <div>{renderOwlFigure()}</div>
        ))}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(NavApp))
